import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut, Search, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { NAV_CONFIG, filterNavForPermissions } from "./app-sidebar-nav";

export function AppSidebar() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user, permissions } = useAuth();
  // US-003b / ADR-017 §3 — nav filtering on the user's resolved permission
  // set (fetched from /api/me/permissions, static fallback until it loads).
  // Items + groups whose navItem isn't granted don't render. With no user
  // the set is empty, so nothing renders until the layout resolves auth.
  const nav = user ? filterNavForPermissions(NAV_CONFIG, permissions.nav_items) : [];
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-gradient-sidebar text-sidebar-foreground border-r border-sidebar-border">
      <div className="p-5 flex items-center gap-2.5 border-b border-sidebar-border">
        <div className="h-9 w-9 rounded-xl bg-gradient-primary grid place-items-center shadow-glow">
          <Sparkles className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold text-sidebar-primary-foreground">ProcureFlow</p>
          <p className="text-[11px] text-sidebar-foreground/60">Vendor Portal</p>
        </div>
      </div>

      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-sidebar-accent/40 px-3 py-2 text-xs text-sidebar-foreground/70">
          <Search className="h-3.5 w-3.5" />
          <span>Search...</span>
          <kbd className="ml-auto rounded bg-sidebar/60 px-1.5 py-0.5 text-[10px]">⌘K</kbd>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
        {nav.map((group) => (
          <div key={group.group}>
            <p className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
              {group.group}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = path === item.to;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-all",
                        active
                          ? "bg-sidebar-primary/15 text-sidebar-primary-foreground shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--sidebar-primary)_30%,transparent)]"
                          : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      )}
                    >
                      <item.icon className={cn("h-4 w-4", active && "text-sidebar-primary")} />
                      <span className="flex-1">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-sidebar-border">
        <div className="flex items-center gap-2.5 rounded-lg p-2 hover:bg-sidebar-accent transition-colors">
          <div className="h-9 w-9 rounded-full bg-gradient-primary grid place-items-center text-primary-foreground text-sm font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-sidebar-primary-foreground">
              {user?.name ?? "—"}
            </p>
            <p className="text-[11px] text-sidebar-foreground/60 truncate">{user?.role ?? "—"}</p>
          </div>
          <Link
            to="/login"
            className="text-sidebar-foreground/60 hover:text-sidebar-primary-foreground"
          >
            <LogOut className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </aside>
  );
}
